<template>
  <div class="programs-panel">
    <div class="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
      <div>
        <div class="fw-bold text-gray-800">Programas</div>
        <div class="text-muted fs-7" v-if="sessionTotals.entries">
          <span v-if="sessionTotals.total">
            Trials: {{ sessionTotals.correct }}/{{ sessionTotals.total }}
            <span v-if="sessionTotals.percent_correct != null">· {{ sessionTotals.percent_correct }}% acerto</span>
            ·
          </span>
          {{ sessionTotals.entries }} registro{{ sessionTotals.entries === 1 ? '' : 's' }} nesta sessão
        </div>
        <div class="text-muted fs-7" v-else>Nenhuma coleta nesta sessão ainda.</div>
      </div>
      <div class="d-flex gap-2">
        <button
          type="button"
          class="btn btn-sm btn-light-primary"
          :disabled="loading || !professionalId || !patientId"
          title="Ver evolução"
          @click="evolutionOpen = true">
          <i class="bi bi-graph-up-arrow"></i> Evolução
        </button>
        <button
          type="button"
          class="btn btn-sm btn-success"
          :disabled="loading || !canEdit"
          @click="showAdd = !showAdd">
          <i class="bi bi-plus-lg"></i> Programa
        </button>
      </div>
    </div>

    <button type="button" class="btn btn-sm btn-light mb-4" @click="showArchived = !showArchived">
      {{ showArchived ? 'Ver ativos' : 'Ver arquivados' }}
    </button>

    <div v-if="showAdd" class="card card-bordered mb-4">
      <div class="card-body py-4">
        <ul class="nav nav-tabs nav-line-tabs mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{ active: addMode === 'library' }" href="#" @click.prevent="addMode = 'library'">
              Biblioteca
            </a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{ active: addMode === 'custom' }" href="#" @click.prevent="addMode = 'custom'">
              Personalizado
            </a>
          </li>
        </ul>

        <div v-if="addMode === 'library'">
          <label class="form-label required">Programa padrão</label>
          <Dropdown
            v-model="selectedTemplateId"
            :options="templateAreas"
            option-label="template_name"
            option-value="id_program_template"
            option-group-label="area"
            option-group-children="templates"
            :filter="true"
            :filter-fields="['template_name', 'template_description']"
            filter-placeholder="Buscar programa..."
            :loading="templatesLoading"
            append-to="self"
            show-clear
            empty-message="Nenhuma opção"
            empty-filter-message="Nenhum programa encontrado"
            placeholder="-- Selecione --"
            class="form-control form-control-solid w-100 programs-template-select"
            style="display: flex; padding: 0.25rem .75rem">
            <template #optiongroup="{ option }">
              <span class="programs-template-group">{{ option.area }}</span>
            </template>
            <template #option="{ option }">
              <div class="d-flex flex-column">
                <span class="fw-semibold text-gray-900">{{ option.template_name }}</span>
                <span class="text-muted fs-8">
                  {{ measurementShort(option.measurement_type) }}
                  <template v-if="option.template_description">· {{ option.template_description }}</template>
                </span>
              </div>
            </template>
          </Dropdown>
          <div class="form-text" v-if="!templateAreas.length && !templatesLoading">
            Nenhum programa padrão disponível para suas especialidades.
          </div>
          <div class="form-text" v-if="selectedTemplate">{{ selectedTemplate.template_description }}</div>

          <div v-if="selectedTemplate && selectedTemplate.measurement_type === 'interval'" class="row g-3 mt-1">
            <div class="col-md-4">
              <label class="form-label">Intervalo (segundos)</label>
              <input v-model.number="newSettings.interval_seconds" type="number" min="5" max="600"
                     class="form-control form-control-solid">
            </div>
            <div class="col-md-5">
              <label class="form-label">Tipo de registro</label>
              <select class="form-select form-select-solid" v-model="newSettings.interval_type">
                <option v-for="option in intervalTypes" :key="option.value" :value="option.value">{{ option.label }}</option>
              </select>
            </div>
          </div>

          <div v-if="selectedTemplate && selectedTemplate.measurement_type === 'percentage'" class="row g-3 mt-1">
            <div class="col-md-4">
              <label class="form-label">Oportunidades por registro</label>
              <input v-model.number="newSettings.opportunities" type="number" min="1" max="1000"
                     class="form-control form-control-solid">
            </div>
          </div>

          <div class="d-flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!selectedTemplateId || saving"
              @click="storeFromTemplate"
            >
              Adicionar
            </button>
            <button type="button" class="btn btn-light" @click="showAdd = false">Cancelar</button>
          </div>
        </div>

        <div v-else class="row g-3">
          <div class="col-md-7">
            <label class="form-label required">Nome do programa</label>
            <input
              v-model="newProgramName"
              type="text"
              class="form-control form-control-solid"
              maxlength="200"
              placeholder="Ex.: Contato visual sob demanda"
              @keyup.enter="storeProgram">
          </div>
          <div class="col-md-5">
            <label class="form-label required">Tipo de medida</label>
            <select class="form-select form-select-solid" v-model="newMeasurementType">
              <option v-for="option in measurementOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
            <div class="form-text">{{ measurementHint(newMeasurementType) }}</div>
          </div>

          <div class="col-md-4" v-if="newMeasurementType === 'interval'">
            <label class="form-label">Intervalo (segundos)</label>
            <input v-model.number="newSettings.interval_seconds" type="number" min="5" max="600"
                   class="form-control form-control-solid">
          </div>
          <div class="col-md-5" v-if="newMeasurementType === 'interval'">
            <label class="form-label">Tipo de registro</label>
            <select class="form-select form-select-solid" v-model="newSettings.interval_type">
              <option v-for="option in intervalTypes" :key="option.value" :value="option.value">{{ option.label }}</option>
            </select>
          </div>
          <div class="col-md-4" v-if="newMeasurementType === 'percentage'">
            <label class="form-label">Oportunidades por registro</label>
            <input v-model.number="newSettings.opportunities" type="number" min="1" max="1000"
                   class="form-control form-control-solid">
          </div>

          <div class="col-12 d-flex flex-wrap gap-2">
            <button type="button" class="btn btn-primary" :disabled="!newProgramName.trim() || saving" @click="storeProgram">
              Salvar
            </button>
            <button type="button" class="btn btn-light" @click="showAdd = false">Cancelar</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center py-8 text-muted">
      <div class="spinner-border spinner-border-sm text-primary me-2"></div>
      Carregando programas...
    </div>

    <div v-else-if="!visiblePrograms.length" class="text-center text-muted py-8">
      <i class="bi bi-bullseye fs-2x mb-3 d-block"></i>
      <div class="fw-semibold mb-1">{{ showArchived ? 'Nenhum programa arquivado' : 'Nenhum programa ativo' }}</div>
      <div v-if="!showArchived" class="fs-7">Cadastre o primeiro programa para começar a coleta nesta sessão.</div>
    </div>

    <div v-else class="d-flex flex-column gap-4">
      <div v-for="program in visiblePrograms" :key="program.id_patient_program" class="card card-bordered">
        <div class="card-body py-4">
          <div class="d-flex justify-content-between align-items-start gap-3 mb-3">
            <div>
              <div class="fw-bold text-gray-900">
                {{ program.program_name }}
                <span class="badge badge-light ms-2">{{ measurementShort(program.measurement_type) }}</span>
              </div>
              <div class="text-muted fs-8" v-if="program.program_description">{{ program.program_description }}</div>

              <div class="d-flex flex-wrap align-items-center gap-4 mt-2">
                <div class="programs-metric">
                  <div class="programs-metric-value">{{ metricValue(program) }}</div>
                  <div class="programs-metric-label">{{ program.session?.metric_label }}</div>
                </div>
                <div class="d-flex flex-wrap gap-3">
                  <div v-for="stat in program.session?.stats || []" :key="stat.label" class="programs-stat">
                    <div class="fw-bold text-gray-800">{{ statValue(stat) }}</div>
                    <div class="text-muted fs-8">{{ stat.label }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="d-flex gap-2">
              <button
                v-if="canEdit && program.active && program.measurement_type === 'interval'"
                type="button"
                class="btn btn-icon btn-sm btn-light"
                title="Configurar intervalo"
                :disabled="saving"
                @click="editIntervalSettings(program)">
                <i class="bi bi-gear"></i>
              </button>
              <button
                v-if="canEdit && program.active"
                type="button"
                class="btn btn-icon btn-sm btn-light-danger"
                title="Arquivar programa"
                :disabled="saving"
                @click="removeProgram(program)">
                <i class="bi bi-archive"></i>
              </button>
            </div>
          </div>

          <button v-if="canEdit && !program.active" type="button" class="btn btn-sm btn-light-primary" :disabled="saving" @click="reactivateProgram(program)">Reativar programa</button>
          <ProgramCollector
            v-if="canEdit && program.active"
            :program="program"
            :saving="saving"
            @record="recordEntry(program, $event)"
            @undo="undoLast(program)"/>
        </div>
      </div>
    </div>
    <ProgramsEvolutionDrawer
      :is-open="evolutionOpen"
      :professional-id="professionalId"
      :patient-id="patientId"
      :place-id="placeId"
      @close="evolutionOpen = false"
    />
  </div>
</template>

<script>
import http from '../../../helpers/http';
import Alert from '../../../helpers/alert';
import Dropdown from 'primevue/dropdown';
import ProgramsEvolutionDrawer from '../shared/ProgramsEvolutionDrawer.vue';
import ProgramCollector from '../shared/ProgramCollector.vue';
import {
  DEFAULT_INTERVAL_SECONDS,
  INTERVAL_TYPES,
  MEASUREMENT_TYPES,
  formatMetricValue,
  formatStatValue,
  measurementShort,
  measurementType,
} from '../../../helpers/programMeasurement';

export default {
  name: 'Programs',
  components: { Dropdown, ProgramsEvolutionDrawer, ProgramCollector },
  props: {
    medicalRecord: { type: Object, required: true },
    schedule: { type: Object, default: null },
  },
  data() {
    return {
      loading: false,
      saving: false,
      showAdd: false,
      addMode: 'library',
      newProgramName: '',
      newMeasurementType: 'trial',
      newSettings: this.defaultSettings(),
      selectedTemplateId: null,
      templateAreas: [],
      templatesLoading: false,
      measurementOptions: MEASUREMENT_TYPES,
      intervalTypes: INTERVAL_TYPES,
      programs: [],
      showArchived: false,
      sessionTotals: { total: 0, correct: 0, incorrect: 0, prompted: 0, entries: 0, percent_correct: null },
      evolutionOpen: false,
    };
  },
  computed: {
    visiblePrograms() {
      return this.programs.filter((program) => this.showArchived ? !program.active : program.active);
    },
    professionalId() {
      return this.schedule?.id_prof || this.medicalRecord?.id_prof;
    },
    patientId() {
      return this.schedule?.id_patient || this.medicalRecord?.id_patient;
    },
    placeId() {
      return this.schedule?.id_place || this.medicalRecord?.id_place || null;
    },
    baseUrl() {
      return `/professionals/${this.professionalId}/patients/${this.patientId}/programs`;
    },
    templatesUrl() {
      return `/professionals/${this.professionalId}/program-templates`;
    },
    canEdit() {
      return !!(this.professionalId && this.patientId && this.medicalRecord?.id_medrec);
    },
    templates() {
      return this.templateAreas.flatMap((area) => area.templates || []);
    },
    selectedTemplate() {
      // <select> serializa o value como string; o id da API vem numérico.
      const selectedId = Number(this.selectedTemplateId);
      if (!selectedId) return null;
      return this.templates.find((template) => Number(template.id_program_template) === selectedId) || null;
    },
  },
  watch: {
    'medicalRecord.id_medrec': {
      immediate: true,
      handler() {
        this.load();
        this.loadTemplates();
      },
    },
    showAdd(open) {
      if (open) {
        this.loadTemplates();
      }
    },
  },
  methods: {
    measurementShort,
    defaultSettings() {
      return {
        interval_seconds: DEFAULT_INTERVAL_SECONDS,
        interval_type: 'partial',
        opportunities: 10,
      };
    },
    measurementHint(type) {
      return measurementType(type)?.hint || '';
    },
    metricValue(program) {
      return formatMetricValue(program.session?.value, program.session?.metric);
    },
    statValue(stat) {
      return formatStatValue(stat);
    },
    /** Só envia as configurações que fazem sentido para o tipo de medida escolhido. */
    settingsFor(type) {
      if (type === 'interval') {
        return {
          interval_seconds: this.newSettings.interval_seconds || DEFAULT_INTERVAL_SECONDS,
          interval_type: this.newSettings.interval_type || 'partial',
        };
      }
      if (type === 'percentage') {
        return { opportunities: this.newSettings.opportunities || 10 };
      }

      return null;
    },
    queryParams() {
      const params = { include_archived: 1 };
      if (this.placeId) params.id_place = this.placeId;
      if (this.medicalRecord?.id_medrec) params.id_medrec = this.medicalRecord.id_medrec;
      return params;
    },
    /** silent: atualiza sem trocar a lista pelo spinner (mantém cronômetros em andamento). */
    load(silent = false) {
      if (!this.professionalId || !this.patientId) return;
      this.loading = !silent;
      http.get(this.baseUrl, { params: this.queryParams() })
        .then((response) => {
          this.programs = response.data?.programs || [];
          this.sessionTotals = response.data?.session_totals || this.sessionTotals;
        })
        .catch((e) => Alert.toastError(e.response?.data?.message ?? e.response?.data ?? e))
        .finally(() => { this.loading = false; });
    },
    loadTemplates() {
      if (!this.professionalId) return;
      this.templatesLoading = true;
      http.get(this.templatesUrl)
        .then((response) => {
          this.templateAreas = response.data?.areas || [];
        })
        .catch(() => { this.templateAreas = []; })
        .finally(() => { this.templatesLoading = false; });
    },
    storeFromTemplate() {
      if (!this.selectedTemplateId) return;
      this.saving = true;
      http.post(this.baseUrl, {
        id_program_template: this.selectedTemplateId,
        id_place: this.placeId,
        program_settings: this.settingsFor(this.selectedTemplate?.measurement_type),
      })
        .then(() => {
          this.selectedTemplateId = null;
          this.newSettings = this.defaultSettings();
          this.showAdd = false;
          this.showArchived = false;
          Alert.toastSuccess('Programa adicionado.');
          this.load();
        })
        .catch((e) => Alert.toastError(e.response?.data?.message ?? e.response?.data ?? e))
        .finally(() => { this.saving = false; });
    },
    storeProgram() {
      const name = this.newProgramName.trim();
      if (!name) return;
      this.saving = true;
      http.post(this.baseUrl, {
        program_name: name,
        measurement_type: this.newMeasurementType || 'trial',
        id_place: this.placeId,
        program_settings: this.settingsFor(this.newMeasurementType),
      })
        .then(() => {
          this.newProgramName = '';
          this.newMeasurementType = 'trial';
          this.newSettings = this.defaultSettings();
          this.showAdd = false;
          this.showArchived = false;
          Alert.toastSuccess('Programa cadastrado.');
          this.load();
        })
        .catch((e) => Alert.toastError(e.response?.data?.message ?? e.response?.data ?? e))
        .finally(() => { this.saving = false; });
    },
    editIntervalSettings(program) {
      const settings = program.program_settings || {};
      const typeOptions = this.intervalTypes
        .map((option) => `<option value="${option.value}" ${settings.interval_type === option.value ? 'selected' : ''}>${option.label}</option>`)
        .join('');

      Swal.fire({
        title: 'Configurar intervalo',
        html: `
          <div class="text-start">
            <label class="form-label fs-7">Intervalo (segundos)</label>
            <input id="swal-interval-seconds" type="number" min="5" max="600" class="form-control form-control-solid mb-3"
                   value="${settings.interval_seconds || DEFAULT_INTERVAL_SECONDS}">
            <label class="form-label fs-7">Tipo de registro</label>
            <select id="swal-interval-type" class="form-select form-select-solid">${typeOptions}</select>
          </div>`,
        showCancelButton: true,
        confirmButtonText: 'Salvar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-secondary',
        },
        preConfirm: () => ({
          interval_seconds: Number(document.getElementById('swal-interval-seconds').value),
          interval_type: document.getElementById('swal-interval-type').value,
        }),
      }).then((result) => {
        if (!result.isConfirmed) return;
        this.saving = true;
        http.put(`${this.baseUrl}/${program.id_patient_program}`, { program_settings: result.value })
          .then(() => {
            Alert.toastSuccess('Configuração atualizada.');
            this.load();
          })
          .catch((e) => Alert.toastError(e.response?.data?.message ?? e.response?.data ?? e))
          .finally(() => { this.saving = false; });
      });
    },
    reactivateProgram(program) {
      this.saving = true;
      http.put(`${this.baseUrl}/${program.id_patient_program}`, { active: true })
        .then(() => {
          this.showArchived = false;
          Alert.toastSuccess('Programa reativado.');
          this.load();
        })
        .catch((e) => Alert.toastError(e.response?.data?.message ?? e.response?.data ?? e))
        .finally(() => { this.saving = false; });
    },
    removeProgram(program) {
      Swal.fire({
        title: 'Arquivar programa?',
        text: 'Ele sai das próximas coletas. O histórico e os gráficos permanecem, e você pode reativá-lo depois.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Arquivar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-secondary',
        },
      }).then((result) => {
        if (!result.isConfirmed) return;
        this.saving = true;
        http.delete(`${this.baseUrl}/${program.id_patient_program}`)
          .then(() => {
            Alert.toastSuccess('Programa arquivado.');
            this.load();
          })
          .catch((e) => Alert.toastError(e.response?.data?.message ?? e.response?.data ?? e))
          .finally(() => { this.saving = false; });
      });
    },
    recordEntry(program, payload) {
      this.saving = true;
      http.post(`${this.baseUrl}/${program.id_patient_program}/entries`, {
        ...payload,
        id_medrec: this.medicalRecord.id_medrec,
        id_sched: this.schedule?.id_sched || this.medicalRecord?.id_sched || null,
        id_place: this.placeId,
      })
        .then(() => this.load(true))
        .catch((e) => Alert.toastError(e.response?.data?.message ?? e.response?.data ?? e))
        .finally(() => { this.saving = false; });
    },
    undoLast(program) {
      if (!program.session?.last_entry_id) return;
      this.saving = true;
      http.delete(`/professionals/${this.professionalId}/patients/${this.patientId}/program-entries/${program.session.last_entry_id}`)
        .then(() => this.load(true))
        .catch((e) => Alert.toastError(e.response?.data?.message ?? e.response?.data ?? e))
        .finally(() => { this.saving = false; });
    },
  },
};
</script>

<style scoped>
.programs-template-select :deep(.p-dropdown-label) {
  padding: 0;
  line-height: 2rem;
}
.programs-template-select :deep(.p-dropdown-label.p-placeholder) {
  color: #a1a5b7;
}
.programs-panel :deep(.p-dropdown-item-group) {
  background: #f1f1f4;
  padding: 8px 12px;
}
.programs-template-group {
  color: #181c32;
  font-weight: 700;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.programs-panel :deep(.p-dropdown-items) {
  padding: 0;
}
.programs-panel :deep(.p-dropdown-item) {
  padding: 8px 12px;
  border-bottom: 1px solid #f4f4f6;
}
.programs-metric-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.1;
  color: #181c32;
}
.programs-metric-label {
  font-size: 0.7rem;
  color: #a1a5b7;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.programs-stat {
  min-width: 62px;
}
</style>
